/**
 * JP main-label version-compare modal — matches the US VersionComparisonModal
 * layout/terminology, adapted for JP:
 *   - summary bar (N total · added · modified · removed · unchanged) + filter tabs
 *   - Expand All / Collapse All, modified sections auto-expanded
 *   - Previous / Current two-column per section, verdict badges
 *   - figures rendered per version (【図:gfname】 → <img> from that version's set)
 *
 * JAPANESE-ONLY diff (decision 2026-09-26): the diff is authoritative on the
 * Japanese source and is the ONLY thing shown here — there is deliberately NO
 * EN/日本語 toggle. English is a per-version translation (drift-prone) so diffing
 * it would show phantom changes; and showing the full English of both versions
 * inside a *diff* view just confuses (no red/green to anchor on). Reading a
 * version in English belongs in the main viewer, which has its own version
 * dropdown + EN/日本語 toggle. Here: English section TITLES for navigation, the
 * Japanese section text with red/green, and JA-driven verdict badges.
 *
 * Terminology (consistent with US): "modified" is a section VERDICT; the per-section
 * counts (+ / −) are the edits WITHIN a modified section (Japanese-diff units).
 *
 * ONE PACKAGE INSERT AT A TIME: a JP product holds several parallel package
 * inserts (lineage = PIN {prefix}_{branch}: holder vs distributor insert, 旧製品
 * old product…), each with its own editions. Two inserts are never compared —
 * the "Package insert" picker chooses the lineage, the version pickers its editions.
 *
 * Data: GET /drugs/{id}/jp-versions (lineages) + /jp-version-diff?lineage=
 * (section-aligned, per-side bilingual HTML + per-version figure maps). History is
 * diff/compare only — never embedded/searched.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { authApi } from '../services/authApi';
import { JpDiffHighlighter } from './viewer/JpDiffHighlighter';
import { injectFigures } from './viewer/JPBilingualSections';

type Filter = 'all' | 'modified' | 'added' | 'removed' | 'unchanged';

export interface JpVersion {
  version_number: number;
  edition: number | null;
  revision_date: string | null;
  is_current: boolean;
  doc_id: number;
  package_insert_no: string | null;
}
/** One package insert of a JP product (``/jp-versions`` ``lineages[]``). */
export interface JpLineage {
  lineage_key: string;
  pin_prefix: string | null;
  branch: string | null;
  brand_name: string | null;
  manufacturer: string | null;
  retired: boolean;
  retired_marker: string | null;
  is_current: boolean;
  versions: JpVersion[]; // newest first
}
interface FigMeta { image_url: string | null; caption: string | null; caption_ja: string | null; }
interface JpDiffSection {
  section_number: string;
  title: string;
  title_ja: string | null;
  verdict: 'added' | 'removed' | 'modified' | 'unchanged';
  old_html: string | null;
  new_html: string | null;
  old_html_ja: string | null;
  new_html_ja: string | null;
  words_added: number;
  words_removed: number;
}
interface JpDiff {
  lineage_key: string;
  from_pin: string | null;
  to_pin: string | null;
  from_version: number;
  to_version: number;
  from_revision_date: string | null;
  to_revision_date: string | null;
  from_figures: Record<string, FigMeta>;
  to_figures: Record<string, FigMeta>;
  summary: { added: number; removed: number; modified: number; unchanged: number; total: number };
  sections: JpDiffSection[];
}
interface Props {
  drugId: number;
  drugName?: string;
  isOpen: boolean;
  onClose: () => void;
  /** Preset the insert + pair (e.g. from the header "Compare: [version]" launcher).
   *  Defaults to the only comparable insert, oldest → newest. */
  initialLineage?: string | null;
  initialFrom?: number | null;
  initialTo?: number | null;
}

const VERDICT_META: Record<string, { label: string; cls: string }> = {
  added: { label: 'Added', cls: 'bg-green-100 text-green-800' },
  removed: { label: 'Removed', cls: 'bg-red-100 text-red-800' },
  modified: { label: 'Modified', cls: 'bg-amber-100 text-amber-800' },
  unchanged: { label: 'Unchanged', cls: 'bg-slate-100 text-slate-500' },
};

const verLabel = (v: JpVersion) =>
  `${v.edition != null ? `ed. ${v.edition}` : `v${v.version_number}`}${v.revision_date ? ` · ${v.revision_date}` : ''}${v.is_current ? ' (current)' : ''}`;

export const lineageLabel = (l: JpLineage) =>
  [l.brand_name, l.manufacturer, l.lineage_key.startsWith('doc:') ? null : l.lineage_key]
    .filter(Boolean).join(' · ') + (l.retired ? ' · Retired' : '');

const figUrlMap = (figs: Record<string, FigMeta> | undefined): Record<string, string> => {
  const out: Record<string, string> = {};
  for (const [g, m] of Object.entries(figs || {})) if (m?.image_url) out[g] = m.image_url;
  return out;
};

export const JPVersionCompareModal: React.FC<Props> = ({
  drugId, drugName, isOpen, onClose, initialLineage, initialFrom, initialTo,
}) => {
  const [lineages, setLineages] = useState<JpLineage[]>([]);
  const [lineage, setLineage] = useState<string | null>(null);
  const [from, setFrom] = useState<number | null>(null);
  const [to, setTo] = useState<number | null>(null);
  const [filter, setFilter] = useState<Filter>('all');
  const [diff, setDiff] = useState<JpDiff | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!isOpen) return;
    let alive = true;
    (async () => {
      try {
        const data = await authApi.getDrugJpVersions(drugId);
        if (!alive) return;
        const comparable: JpLineage[] = (data.lineages || []).filter(
          (l: JpLineage) => l.versions.length >= 2);
        setLineages(comparable);
        const preset = comparable.find((l) => l.lineage_key === initialLineage);
        const lin = preset ?? comparable[0];
        if (lin) {
          // the preset pair only applies to the insert it was chosen in
          setLineage(lin.lineage_key);
          setFrom((preset && initialFrom) ?? lin.versions[lin.versions.length - 1]!.version_number);
          setTo((preset && initialTo) ?? lin.versions[0]!.version_number);
        }
      } catch { if (alive) setError('Could not load JP versions.'); }
    })();
    return () => { alive = false; };
  }, [isOpen, drugId, initialLineage, initialFrom, initialTo]);

  const versions = useMemo(
    () => lineages.find((l) => l.lineage_key === lineage)?.versions ?? [], [lineages, lineage]);
  // Switching insert resets the pair to that insert's oldest → newest, in one
  // render, so a diff is never requested with another insert's version numbers.
  const pickLineage = (key: string) => {
    const l = lineages.find((x) => x.lineage_key === key);
    if (!l) return;
    setLineage(key);
    setFrom(l.versions[l.versions.length - 1]!.version_number);
    setTo(l.versions[0]!.version_number);
  };

  useEffect(() => {
    if (!isOpen || lineage == null || from == null || to == null || from === to) return;
    let alive = true;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const data = await authApi.getDrugJpVersionDiff(drugId, lineage, from, to);
        if (!alive) return;
        setDiff(data);
        // auto-expand the modified/added/removed sections (like US)
        setExpanded(new Set(
          (data.sections || [])
            .filter((s: JpDiffSection) => s.verdict !== 'unchanged')
            .map((s: JpDiffSection) => s.section_number)));
      } catch {
        if (alive) setError('Could not load the diff for this version pair.');
      } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [isOpen, drugId, lineage, from, to]);

  const fromFigs = useMemo(() => figUrlMap(diff?.from_figures), [diff]);
  const toFigs = useMemo(() => figUrlMap(diff?.to_figures), [diff]);

  const visible = useMemo(() => {
    const secs = diff?.sections || [];
    return filter === 'all' ? secs : secs.filter((s) => s.verdict === filter);
  }, [diff, filter]);

  // Versions newest-first, and the older-left / newer-right constraint:
  // the OLDER picker only offers versions below the NEWER selection, and
  // vice-versa — so you can never put the newer one on the left.
  const byNumDesc = useMemo(
    () => [...versions].sort((a, b) => b.version_number - a.version_number), [versions]);
  const fromOptions = useMemo(
    () => byNumDesc.filter((v) => to == null || v.version_number < to), [byNumDesc, to]);
  const toOptions = useMemo(
    () => byNumDesc.filter((v) => from == null || v.version_number > from), [byNumDesc, from]);

  if (!isOpen) return null;

  // English title for navigation (fall back to Japanese if a section has none).
  const titleOf = (s: JpDiffSection) => s.title || s.title_ja || '';
  const toggle = (n: string) => setExpanded((p) => {
    const s = new Set(p); s.has(n) ? s.delete(n) : s.add(n); return s;
  });
  const expandAll = () => setExpanded(new Set((diff?.sections || []).map((s) => s.section_number)));
  const collapseAll = () => setExpanded(new Set());

  const sm = diff?.summary;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[92vh] flex flex-col"
           onClick={(e) => e.stopPropagation()}>
        {/* Header — EU-style: title + OLDER (left) → NEWER (right) version pickers */}
        <div className="flex items-center gap-3 border-b px-5 py-3 flex-wrap">
          <div className="flex items-baseline gap-2 flex-shrink-0">
            <h2 className="text-base font-semibold text-slate-800">{drugName || 'Compare'}</h2>
            <span className="text-xs text-slate-400">· historical Japanese package insert</span>
          </div>
          {lineages.length > 1 && (
            <select
              aria-label="Package insert"
              className="border border-slate-300 rounded-lg px-3 py-1.5 text-sm bg-white max-w-[22rem]"
              value={lineage ?? ''} onChange={(e) => pickLineage(e.target.value)}
            >
              {lineages.map((l) => <option key={l.lineage_key} value={l.lineage_key}>{lineageLabel(l)}</option>)}
            </select>
          )}
          <div className="flex items-center gap-2">
            <select
              aria-label="Older version"
              className="border border-slate-300 rounded-lg px-3 py-1.5 text-sm bg-white"
              value={from ?? ''} onChange={(e) => setFrom(Number(e.target.value))}
            >
              {fromOptions.map((v) => <option key={v.version_number} value={v.version_number}>{verLabel(v)}</option>)}
            </select>
            <span className="text-slate-400">→</span>
            <select
              aria-label="Newer version"
              className="border border-slate-300 rounded-lg px-3 py-1.5 text-sm bg-white"
              value={to ?? ''} onChange={(e) => setTo(Number(e.target.value))}
            >
              {toOptions.map((v) => <option key={v.version_number} value={v.version_number}>{verLabel(v)}</option>)}
            </select>
          </div>
          <div className="ml-auto flex items-center gap-3">
            {diff?.from_pin && diff?.to_pin && (
              <span className="text-[11px] text-slate-500">PIN {diff.from_pin} → {diff.to_pin}</span>
            )}
            <span className="text-[11px] text-slate-400">日本語 diff · read a version in the viewer</span>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-700 text-2xl leading-none">×</button>
          </div>
        </div>

        {/* Summary bar + filter tabs + expand/collapse (US parity) */}
        {sm && (
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 border-b px-5 py-2 text-xs">
            <span className="text-slate-600">
              <b>{sm.total}</b> sections total
              <span className="text-green-700 ml-2">{sm.added} added</span>
              <span className="text-amber-700 ml-2">{sm.modified} modified</span>
              <span className="text-red-700 ml-2">{sm.removed} removed</span>
              <span className="text-slate-400 ml-2">{sm.unchanged} unchanged</span>
            </span>
            <div className="inline-flex gap-1">
              {(['all', 'modified', 'added', 'removed', 'unchanged'] as Filter[]).map((f) => (
                <button key={f} onClick={() => setFilter(f)}
                        className={`px-2 py-1 rounded capitalize ${filter === f ? 'bg-slate-800 text-white' : 'bg-slate-100 text-slate-600'}`}>
                  {f}
                </button>
              ))}
            </div>
            <div className="ml-auto inline-flex gap-2">
              <button onClick={expandAll} className="text-indigo-600 hover:underline">Expand All</button>
              <button onClick={collapseAll} className="text-slate-500 hover:underline">Collapse All</button>
            </div>
          </div>
        )}

        {/* Body */}
        <div className="overflow-y-auto px-5 py-3 space-y-2">
          {loading && <p className="text-slate-500 text-sm">Computing diff…</p>}
          {error && <p className="text-red-600 text-sm">{error}</p>}
          {!loading && !error && versions.length < 2 && (
            <p className="text-slate-500 text-sm">No package insert of this product has more than one version — nothing to compare yet.</p>
          )}
          {!loading && !error && diff && visible.length === 0 && (
            <p className="text-slate-500 text-sm">No sections match this filter.</p>
          )}
          {!loading && !error && diff && visible.map((s) => {
            const meta = VERDICT_META[s.verdict] ?? { label: s.verdict, cls: 'bg-slate-100 text-slate-500' };
            const isOpenRow = expanded.has(s.section_number);
            const oldHtml = injectFigures(s.old_html_ja || '', fromFigs);
            const newHtml = injectFigures(s.new_html_ja || '', toFigs);
            return (
              <div key={s.section_number} className="border rounded-md">
                <button onClick={() => toggle(s.section_number)}
                        className="w-full flex items-center gap-2 bg-slate-50 hover:bg-slate-100 px-3 py-2 text-left">
                  <span className="text-slate-400 text-xs w-3">{isOpenRow ? '▾' : '▸'}</span>
                  <span className="font-mono text-xs text-slate-500">§{s.section_number}</span>
                  <span className="font-medium text-sm text-slate-800">{titleOf(s)}</span>
                  <span className={`ml-2 text-[10px] px-2 py-0.5 rounded-full ${meta.cls}`}>{meta.label}</span>
                  {(s.words_added > 0 || s.words_removed > 0) && (
                    <span className="ml-auto text-[11px] text-slate-500">
                      <span className="text-green-700">+{s.words_added}</span>{' '}
                      <span className="text-red-700">−{s.words_removed}</span>
                    </span>
                  )}
                </button>
                {isOpenRow && (
                  s.verdict === 'unchanged' ? (
                    <div className="px-3 py-2 text-xs text-slate-400">No changes in this section.</div>
                  ) : (
                    <div className="p-3">
                      <div className="grid grid-cols-2 gap-2 text-[11px] mb-1 px-1">
                        <span>
                          <span className="bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded uppercase tracking-wide mr-1">Older</span>
                          <span className="text-slate-500">{diff.from_pin ?? `v${diff.from_version}`}{diff.from_revision_date ? ` · ${diff.from_revision_date}` : ''}</span>
                        </span>
                        <span>
                          <span className="bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded uppercase tracking-wide mr-1">Newer</span>
                          <span className="text-slate-500">{diff.to_pin ?? `v${diff.to_version}`}{diff.to_revision_date ? ` · ${diff.to_revision_date}` : ''}</span>
                        </span>
                      </div>
                      <JpDiffHighlighter oldHtml={oldHtml} newHtml={newHtml} charLevel />
                    </div>
                  )
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default JPVersionCompareModal;
